import { Component, OnInit, computed, inject, HostListener, signal, OnDestroy } from '@angular/core';
import { SrAvisoCobranzaComponent } from '../shared/components/sr-aviso-cobranza/sr-aviso-cobranza.component';
import { CommonModule } from '@angular/common';
import { ActivatedRouteSnapshot, Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { TopbarComponent } from './topbar/topbar.component';
import { FooterComponent } from './footer/footer.component';
import { BottomNavComponent } from './bottom-nav/bottom-nav.component';
import { EmpresaLogoService } from '../core/services/empresa-logo.service';
import { InstanceConfigService } from '../core/services/instance-config.service';
import { MenuService } from './services/menu.service';
import {
  SrSidebarComponent, SrSideSectionComponent, SrSideItemComponent, SrSideCollapsibleComponent,
} from '../shared/components/sr-sidebar/sr-sidebar.component';
import { SrMaquiComponent } from '../shared/components/sr-maqui/sr-maqui.component';
import { AuthService } from '../features/auth/services/auth.service';
import { OperadorService } from '../core/operador/operador.service';
import { SrOperadorGateComponent } from '../shared/components/sr-operador-gate/sr-operador-gate.component';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    SrAvisoCobranzaComponent,
    CommonModule,
    RouterOutlet,
    TopbarComponent,
    FooterComponent,
    BottomNavComponent,
    SrSidebarComponent,
    SrSideSectionComponent,
    SrSideItemComponent,
    SrSideCollapsibleComponent,
    SrMaquiComponent,
    SrOperadorGateComponent,
  ],
  template: `
    <div class="layout-wrapper">
      <!-- Overlay para cerrar sidebar en mobile -->
      @if (isMobile() && !sidebarCollapsed) {
        <div class="sidebar-overlay" (click)="closeSidebar()"></div>
      }

      <!-- Sidebar v16 (sr-sidebar): drawer off-canvas en tablet 768-991px,
           rail de 60px en 992-1023px, full 240px en desktop, oculto <768px.
           El panel de plataforma va en riel desde 768 (mockup 29-oct): tiene
           cinco entradas, y esconderlas detrás de la hamburguesa no ahorra nada. -->
      @if (!immersive() && !isMobileSmall()) {
        <sr-sidebar [collapsed]="sidebarCollapsed && (!isMobile() || rielEnTablet())"
                    [drawer]="isMobile() && !rielEnTablet()"
                    [open]="!sidebarCollapsed">
          @for (section of menuSections(); track section.label || '__root') {
            @if (section.label) {
              <sr-side-section [label]="section.label">
                @for (item of section.items; track item.label) {
                  @if (item.children) {
                    <sr-side-collapsible
                      [icon]="item.icon" [label]="item.label"
                      [count]="item.count" [severity]="item.severity || 'default'"
                      [expanded]="!!item.expanded"
                    >
                      @for (child of item.children; track child.label) {
                        <sr-side-item
                          [icon]="child.icon" [label]="child.label"
                          [route]="child.route" [queryParams]="child.queryParams"
                          [count]="child.count" [severity]="child.severity || 'default'"
                        />
                      }
                    </sr-side-collapsible>
                  } @else {
                    <sr-side-item
                      [icon]="item.icon" [label]="item.label"
                      [route]="item.route" [queryParams]="item.queryParams"
                      [count]="item.count" [severity]="item.severity || 'default'"
                      [exact]="!!item.exact"
                    />
                  }
                }
              </sr-side-section>
            } @else {
              @for (item of section.items; track item.label) {
                <sr-side-item
                  [icon]="item.icon" [label]="item.label"
                  [route]="item.route" [exact]="!!item.exact"
                />
              }
            }
          }
        </sr-sidebar>
      }

      <div class="layout-main"
           [class.layout-main-expanded]="isMobileSmall()"
           [class.layout-main-collapsed]="sidebarCollapsed && !isMobile()"
           [class.layout-main-tablet]="isTablet()"
           [class.layout-main-riel]="rielEnTablet() && isMobile()"
           [class.layout-main-immersive]="immersive()">
        @if (!immersive()) {
          <app-topbar
            (toggleSidebar)="toggleSidebar()"
            [sidebarCollapsed]="sidebarCollapsed"
            [isMobile]="isMobileSmall()">
          </app-topbar>
        }

        <!-- Bajo la topbar y sobre el contenido: tiene que verse haga lo que
             haga el usuario, no solo si entra a facturación. -->
        @if (!immersive()) {
          <sr-aviso-cobranza />
        }

        <div class="layout-content fade-in">
          <router-outlet></router-outlet>
        </div>

        @if (!immersive()) {
          <app-footer></app-footer>
        }
      </div>

      <!-- Bottom Nav: solo visible en mobile < 768px -->
      @if (!immersive() && isMobileSmall()) {
        <app-bottom-nav></app-bottom-nav>
      }

      <!-- Asistente IA Maqui (#648): FAB + drawer. Se auto-oculta si no está habilitado. -->
      @if (!immersive()) {
        <sr-maqui></sr-maqui>
      }

      <!-- Mecánico por PIN: tapa el trabajo hasta que alguien pone su PIN. No dibuja nada si el taller no lo usa. -->
      <sr-operador-gate />
    </div>
  `,
  styles: [`
    .sidebar-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.5);
      z-index: var(--z-fixed);
      animation: fadeIn 0.2s ease-out;
    }

    .layout-main-immersive {
      margin-left: 0;
      padding-top: 0;
    }

    /* Riel del panel de plataforma entre 768 y 991: el global deja el contenido sin margen en ese tramo
       (ahí manda el cajón); con riel, el contenido empieza después de sus 60 px. */
    @media (min-width: 768px) and (max-width: 991px) {
      .layout-main-riel { margin-left: 60px !important; }
    }

    .layout-main-immersive .layout-content {
      overflow: hidden;
      padding: 0;
      scrollbar-gutter: auto;
    }

    @media (max-width: 767px) {
      .layout-main-immersive {
        padding-top: 0 !important;
      }

      .layout-main-immersive .layout-content {
        padding: 0 !important;
      }
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
  `]
})
export class LayoutComponent implements OnInit, OnDestroy {
  private logoService = inject(EmpresaLogoService);
  private router = inject(Router);
  private menuService = inject(MenuService);
  private authService = inject(AuthService);
  private operador = inject(OperadorService);
  private routerSub?: Subscription;

  private instanceConfig = inject(InstanceConfigService);

  /**
   * Estructura del menú v16 (Lucide icons + counts) para sr-sidebar.
   * computed: se re-construye cuando llega la config de instancia (#553) —
   * buildSrSidebarMenu() lee el signal de capacidades por dentro.
   */
  readonly menuSections = computed(() => this.menuService.buildSrSidebarMenu());

  /** Solo el panel de plataforma: las cuentas de clientes siguen con el cajón en ese ancho. */
  readonly rielEnTablet = computed(() => this.instanceConfig.esPanelPlataforma());

  sidebarCollapsed = false;
  isMobile = signal(false);
  isMobileSmall = signal(false);
  isTablet = signal(false);
  immersive = signal(false);

  /**
   * Lo que un operador de terreno sí puede abrir dentro del shell de gestión.
   *
   * <p>Es una guarda de experiencia, no de seguridad: quien manda es
   * `@PreAuthorize` en el backend y `data.roles` de cada ruta. Sirve para no
   * mandar a la cuenta a una pantalla que le va a responder 403 o la va a
   * rebotar por rol.
   *
   * <p>El técnico (ROLE_TECNICO, #853) tiene taller y equipos; un operador
   * de horómetro solo el taller, si es que llegó ahí de alguna forma —el
   * horómetro no pasa por acá normalmente.
   */
  private get RUTAS_DE_TERRENO(): string[] {
    // «/perfil» también: es donde el técnico ve su firma y, con PIN, su perfil de mecánico.
    return this.authService.esDelTaller() && this.instanceConfig.muestraArriendo()
      ? ['/servicio-tecnico', '/equipos', '/perfil']
      : ['/servicio-tecnico', '/perfil'];
  }

  /**
   * A dónde iba el usuario.
   *
   * <p>En `ngOnInit` del shell el router todavía puede reportar `/`: la
   * navegación que activó este componente no terminó. La barra de direcciones
   * sí tiene el destino, así que se usa como respaldo.
   */
  private destinoActual(): string {
    const delRouter = this.router.url;
    if (delRouter && delRouter !== '/') return delRouter;
    return typeof window !== 'undefined' ? window.location.pathname : delRouter;
  }

  private esRutaDeTerreno(url: string): boolean {
    const limpia = url.split('?')[0];
    return this.RUTAS_DE_TERRENO.some(r => limpia === r || limpia.startsWith(r + '/'));
  }

  /**
   * El espacio que `scrollbar-gutter: stable` le reserva a la barra de desplazamiento, en `--sr-ancho-barra`.
   * `.layout-content` lo reserva a la derecha, pero solo rige en un contenedor con scroll. El detalle con navegación
   * entre registros deja de serlo (su fila fija se pega a la ventana) y reserva el mismo espacio con margen, para
   * medir lo mismo de ancho que las demás vistas. Se mide con el mismo mecanismo, así da lo que el navegador
   * reserva: 0 con barras flotantes (macOS por defecto).
   */
  private medirAnchoBarra(): void {
    const prueba = document.createElement('div');
    prueba.style.cssText =
      'position:absolute;top:-9999px;width:100px;height:100px;overflow:auto;scrollbar-gutter:stable';
    document.body.appendChild(prueba);
    const ancho = prueba.offsetWidth - prueba.clientWidth;
    prueba.remove();
    document.documentElement.style.setProperty('--sr-ancho-barra', `${ancho}px`);
  }

  private readonly MOBILE_BREAKPOINT = 992;
  private readonly MOBILE_SMALL_BREAKPOINT = 768;
  private readonly TABLET_BREAKPOINT = 1024;

  ngOnInit() {
    // Los operadores de terreno no usan el shell de gestión: van directo a la
    // PWA. La excepción es el taller: ahí el mecánico ES el usuario —recibe la
    // máquina, saca las fotos, anota el trabajo y avisa cuando termina—, y los
    // endpoints son TERRENO justamente por eso. Sin esta salvedad el módulo
    // quedaba inalcanzable para quien tiene que usarlo.
    if (this.authService.isFieldAccount() && !this.esRutaDeTerreno(this.destinoActual())) {
      const destino = this.authService.esDelTaller() ? this.authService.inicioDelTaller() : '/operador';
      this.router.navigate([destino], { replaceUrl: true });
      return;
    }

    this.checkScreenSize();
    this.medirAnchoBarra();
    this.updateImmersiveMode();
    void this.operador.iniciar();
    this.logoService.loadLogo();
    // Config de instancia (#553): actualiza el signal → el menú (computed)
    // se re-filtra solo. Fail-safe COMPLETO si falla (el backend bloquea igual).
    this.instanceConfig.cargar().subscribe();

    // Cerrar sidebar en mobile al navegar
    this.routerSub = this.router.events.pipe(
      filter(e => e instanceof NavigationEnd)
    ).subscribe(() => {
      this.updateImmersiveMode();
      if (this.isMobile()) {
        this.sidebarCollapsed = true;
      }
    });
  }

  ngOnDestroy() {
    this.routerSub?.unsubscribe();
  }

  @HostListener('window:resize')
  onResize() {
    this.checkScreenSize();
  }

  private checkScreenSize() {
    const wasMobile = this.isMobile();
    const wasTablet = this.isTablet();
    const width = window.innerWidth;
    const isNowMobile = width < this.MOBILE_BREAKPOINT;
    const isNowMobileSmall = width < this.MOBILE_SMALL_BREAKPOINT;
    const isNowTablet = width >= this.MOBILE_SMALL_BREAKPOINT && width < this.TABLET_BREAKPOINT;

    this.isMobile.set(isNowMobile);
    this.isMobileSmall.set(isNowMobileSmall);
    this.isTablet.set(isNowTablet);

    // Auto-colapsar sidebar cuando cambia a mobile o tablet por primera vez
    if (!wasMobile && isNowMobile) {
      this.sidebarCollapsed = true;
    }
    if (!wasTablet && isNowTablet) {
      this.sidebarCollapsed = true;
    }

    // Auto-expandir sidebar cuando cambia a desktop
    if (wasMobile && !isNowMobile && !isNowTablet) {
      this.sidebarCollapsed = false;
    }
  }

  toggleSidebar() {
    this.sidebarCollapsed = !this.sidebarCollapsed;
  }

  closeSidebar() {
    if (this.isMobile()) {
      this.sidebarCollapsed = true;
    }
  }

  private updateImmersiveMode(): void {
    let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    let immersive = false;
    while (route) {
      immersive ||= route.data['immersive'] === true;
      route = route.firstChild ?? null;
    }
    this.immersive.set(immersive);
  }

}
